/**
 * Agent Service for Jira Simulation Agent
 * 
 * Handles API calls to backend for parsing responses, generating JSON,
 * uploading files, and creating Jira tickets
 */

import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasFetch } from '../api/atlasBackendClient';
import { CollectedAnswers } from './questionFlowEngine';
import type {
  RAGQueryRequest,
  RAGQueryResponse,
  GetSuggestionsRequest,
  GetSuggestionsResponse,
  ValidateAnswerRequest,
  ValidateAnswerResponse,
} from '../../types/rag';
import type {
  ConversationQueryRequest,
  ConversationQueryResponse,
  ConversationContext,
} from './conversationStateService';
import { intentService } from './intentService';
import { conversationStateService } from './conversationStateService';

/**
 * Read a single error message from a failed Atlas API response without consuming the body twice.
 * Surfaces clearer text for gateway errors (often shown as "CORS" in DevTools when ALB returns HTML).
 */
async function readFailedAtlasResponseMessage(response: Response): Promise<string> {
  const status = response.status;
  if (status === 502 || status === 503 || status === 504) {
    return `Atlas backend returned ${status} (gateway/upstream unavailable). If DevTools shows a CORS error, the proxy response often omits CORS headers — check deployment / target health for ${response.url.split('?')[0]}.`;
  }
  const raw = await response.text();
  if (!raw.trim()) {
    return `HTTP ${status}`;
  }
  try {
    const data = JSON.parse(raw) as { detail?: unknown; error?: unknown };
    const detail = typeof data.detail === 'string' ? data.detail : undefined;
    const err = typeof data.error === 'string' ? data.error : undefined;
    return detail || err || `HTTP ${status}`;
  } catch {
    const snippet = raw.replace(/\s+/g, ' ').trim().slice(0, 200);
    return snippet || `HTTP ${status}`;
  }
}

export interface ParseResponseRequest {
  question_context: string;
  user_message: string;
  conversation_history?: Array<{ role: 'user' | 'agent'; content: string }>;
}

export interface ParseResponseResult {
  extracted_value: any;
  confidence: number;
  clarification_needed?: boolean;
  clarification_message?: string;
}

export interface GenerateJsonRequest {
  answers: CollectedAnswers;
}

export interface GenerateJsonResult {
  json: any;
  validation_errors: string[];
  warnings: string[];
}

export interface UploadFileRequest {
  file: File;
}

export interface UploadFileResult {
  file_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  parsed_data?: any; // KML points/hubs or Excel column headers
}

export interface UploadCapabilitiesResult {
  max_upload_file_bytes: number;
  supported_extensions: string[];
}

export interface CreateJiraTicketRequest {
  title: string;
  description: string;
  project_key: string;
  issue_type?: string;
  file_ids?: string[];
}

export interface CreateJiraTicketResult {
  ticket_key: string;
  ticket_url: string;
  ticket_id: string;
  attached_files?: Array<{
    file_id: string;
    file_name: string;
    attachment_id?: string;
    success: boolean;
    error?: string;
  }>;
  errors?: Array<{
    file_id: string;
    file_name?: string;
    error: string;
  }>;
}

export class AgentService {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = getAtlasApiBaseUrl();
  }
  
  /**
   * Parse user response using LLM
   */
  async parseResponse(
    questionContext: string,
    userMessage: string,
    conversationHistory?: Array<{ role: 'user' | 'agent'; content: string }>
  ): Promise<ParseResponseResult> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/agent/parse-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question_context: questionContext,
          user_message: userMessage,
          conversation_history: conversationHistory || [],
        } as ParseResponseRequest),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error parsing response:', error);
      throw error;
    }
  }
  
  /**
   * Generate simulation JSON from collected answers
   */
  async generateJson(answers: CollectedAnswers): Promise<GenerateJsonResult> {
    try {
      // Log what we're sending
      console.log('🔍 AgentService.generateJson - Sending answers:', {
        fleet_id: answers.fleet_id,
        vehicle_method: answers.vehicle_method,
        has_fleet_data: !!(answers as any).fleet_data,
        fleet_data_vehicles: (answers as any).fleet_data?.vehicles?.length || 0,
      });
      if ((answers as any).fleet_data) {
        console.log('🔍 AgentService.generateJson - fleet_data:', JSON.stringify((answers as any).fleet_data, null, 2));
      }
      
      const response = await atlasFetch(`${this.baseUrl}/api/agent/generate-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          answers,
        } as GenerateJsonRequest),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error generating JSON:', error);
      throw error;
    }
  }
  
  /**
   * Query RAG system for answers and suggestions
   */
  async queryRAG(
    query: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>,
    currentContext?: {
      current_question?: string;
      collected_answers?: Record<string, any>;
      question_phase?: string;
    }
  ): Promise<RAGQueryResponse> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/agent/query-rag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          conversation_history: conversationHistory || [],
          current_context: currentContext,
        } as RAGQueryRequest),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as RAGQueryResponse;
    } catch (error) {
      console.error('Error querying RAG:', error);
      throw error;
    }
  }
  
  /**
   * Query conversation (conversational query with state management)
   */
  async queryConversational(
    query: string,
    conversationId?: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
    context?: ConversationContext
  ): Promise<ConversationQueryResponse> {
    return conversationStateService.queryConversation(
      query,
      conversationId,
      conversationHistory,
      context
    );
  }
  
  /**
   * Classify user intent from a message
   */
  async classifyIntent(
    message: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<{ intent: string; confidence: number; reasoning: string }> {
    return intentService.classifyIntent(message, conversationHistory);
  }
  
  /**
   * Get proactive suggestions based on current state
   */
  async getSuggestions(
    collectedAnswers: Record<string, any>,
    currentQuestion?: string,
    context?: Record<string, any>
  ): Promise<GetSuggestionsResponse> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/agent/get-suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          collected_answers: collectedAnswers,
          current_question: currentQuestion,
          context,
        } as GetSuggestionsRequest),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as GetSuggestionsResponse;
    } catch (error) {
      console.error('Error getting suggestions:', error);
      throw error;
    }
  }
  
  /**
   * Validate answer against best practices
   */
  async validateAnswer(
    question: string,
    answer: any,
    collectedAnswers: Record<string, any>
  ): Promise<ValidateAnswerResponse> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/agent/validate-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question,
          answer,
          collected_answers: collectedAnswers,
        } as ValidateAnswerRequest),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as ValidateAnswerResponse;
    } catch (error) {
      console.error('Error validating answer:', error);
      throw error;
    }
  }
  
  /**
   * Get upload capabilities from backend so frontend validation matches active deployment.
   */
  async getUploadCapabilities(): Promise<UploadCapabilitiesResult> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/agent/upload-capabilities`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(await readFailedAtlasResponseMessage(response));
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching upload capabilities:', error);
      throw error;
    }
  }

  /**
   * Upload a file
   */
  async uploadFile(file: File): Promise<UploadFileResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const url = `${this.baseUrl}/api/agent/upload-file`;
      console.log('Uploading file to:', url);
      console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
      
      const response = await atlasFetch(url, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
      });
      
      if (!response.ok) {
        throw new Error(await readFailedAtlasResponseMessage(response));
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error(`Failed to connect to backend server. Please ensure the backend is running at ${this.baseUrl}`);
      }
      throw error;
    }
  }
  
  /**
   * Get file info by ID
   */
  async getFile(fileId: string): Promise<UploadFileResult> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/agent/file/${fileId}`, {
        method: 'GET',
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting file:', error);
      throw error;
    }
  }
  
  /**
   * Create a Jira ticket
   */
  async createJiraTicket(
    title: string,
    description: string,
    projectKey: string = 'NEWMOB',
    issueType: string = 'Task',
    fileIds?: string[]
  ): Promise<CreateJiraTicketResult> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/jira/create-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          description,
          project_key: projectKey,
          issue_type: issueType,
          file_ids: fileIds || [],
        } as CreateJiraTicketRequest),
      });
      
      if (!response.ok) {
        const errorDetail = await readFailedAtlasResponseMessage(response);
        console.error('Jira API error response:', errorDetail);
        throw new Error(errorDetail);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating Jira ticket:', error);
      throw error;
    }
  }
  
  /**
   * Attach file to Jira ticket
   */
  async attachFileToJira(ticketKey: string, fileId: string): Promise<void> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/jira/attach-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          issue_key: ticketKey,
          file_id: fileId,
        }),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error attaching file to Jira:', error);
      throw error;
    }
  }
}

